import InputFile, { UploadFileProps } from '@anm/components/InputFile';
import cutFileExtension from '@anm/helpers/cutFileExtension';
import isAllowedExtension from '@anm/helpers/is/isAllowedExtension';
import noop from '@anm/helpers/noop';
import { appNotificationActions } from '@anm/store/modules/appNotifications';
import { CreateUploadProps } from '@anm/store/modules/uploads';
import { media } from '@anm/styles/helpers';
import React, { useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { userSelectors } from '../../../store/modules/user';

import vtConfig from '../../../config';
import useStorageErrorModal from '../../../hooks/useStorageErrorModal';

import { useUploadRetry } from './hooks';

const buttonOnMobile = media.phone`
  max-width: 100%;
`;

const Wrapper = styled.label<{ disabled?: boolean }>`
  div {
    ${buttonOnMobile};
  }

  & > * {
    ${({ disabled }) =>
      disabled &&
      css`
        opacity: 0.7;
        cursor: default;
        pointer-events: none;
      `}
  }
`;

type UploadVideoButtonProps = {
  disabled?: boolean;
  onUploadStart?: () => void;
};

const {
  supportedVideoExtensions,
  uploadVideoDefaults: { metaInfo }
} = vtConfig();

const allowedExtensionsList = supportedVideoExtensions.map(e => `.${e}`).join();

const UploadVideoButton: FC<UploadVideoButtonProps> = ({ children, disabled, onUploadStart = noop }) => {
  const [createVideoParams, setCreateVideoParams] = useState<CreateUploadProps>();
  const createVideo = useUploadRetry(createVideoParams);
  const dispatch = useDispatch();

  const hostingStats = useSelector(userSelectors.selectUserHostingStats)?.data || null;
  const user = useSelector(userSelectors.selectUser);

  const handleFileChange = async (props: UploadFileProps[]) => {
    const { file, objectUrl } = props[0];
    const name = cutFileExtension(file.name);
    const description = `${name} - ${metaInfo.description}`;

    if (!isAllowedExtension(file.name, allowedExtensionsList)) {
      dispatch(
        appNotificationActions.notify({
          type: 'error',
          title: 'This file type is not supported'
        })
      );
      return;
    }

    onUploadStart();

    const params: CreateUploadProps = {
      user: user.profile,
      hostingStats,
      createHost: true,
      uploadType: 'new',
      isUploadFile: true,
      uploads: [
        {
          file,
          objectUrl,
          meta: { name, description }
        }
      ]
    };

    setCreateVideoParams(params);
    createVideo(params);
  };

  const renderStorageModal = useStorageErrorModal({ actionType: 'upload' });

  return (
    <Wrapper disabled={disabled}>
      {renderStorageModal()}
      <InputFile handleUpload={handleFileChange} accept={allowedExtensionsList} disabled={disabled} />
      {children}
    </Wrapper>
  );
};

export default UploadVideoButton;
